import { useEffect, useRef } from 'react'

const FOCUSABLE =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Keyboard behaviour every modal dialog needs: Escape closes it, focus moves
 * into it when it opens, Tab stays inside, and focus returns to whatever
 * opened it when it closes. Attach the returned ref to the element that
 * carries role="dialog".
 */
const useDialogBehavior = onClose => {
	const dialogRef = useRef(null)
	const onCloseRef = useRef(onClose)
	onCloseRef.current = onClose

	useEffect(() => {
		const dialog = dialogRef.current
		if (!dialog) return undefined
		const opener = document.activeElement
		const focusables = () => Array.from(dialog.querySelectorAll(FOCUSABLE))

		if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1')
		const [first] = focusables()
		;(first || dialog).focus({ preventScroll: true })

		const onKeyDown = event => {
			if (event.key === 'Escape') {
				event.stopPropagation()
				onCloseRef.current?.()
				return
			}
			if (event.key !== 'Tab') return
			const items = focusables()
			if (items.length === 0) {
				event.preventDefault()
				return
			}
			const firstItem = items[0]
			const lastItem = items[items.length - 1]
			if (
				event.shiftKey &&
				(document.activeElement === firstItem || document.activeElement === dialog)
			) {
				event.preventDefault()
				lastItem.focus()
			} else if (!event.shiftKey && document.activeElement === lastItem) {
				event.preventDefault()
				firstItem.focus()
			}
		}

		document.addEventListener('keydown', onKeyDown)
		return () => {
			document.removeEventListener('keydown', onKeyDown)
			if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
				opener.focus({ preventScroll: true })
			}
		}
	}, [])

	return dialogRef
}

export default useDialogBehavior
